import {
  Home as HomeIcon,
  Map as MapIcon,
  Notifications as NotificationsIcon,
  Person as ProfileIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Luggage as TripsIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  IconButton,
  Paper,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useRef, useState, type ReactNode, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { LogoLockup } from '@/components/brand/PintravelLogo';
import { FailedChangesDialog } from '@/components/feedback/FailedChangesDialog';
import { SyncStatusIndicator } from '@/components/feedback/SyncStatusIndicator';
import UserAvatar from '@/components/ui/UserAvatar';
import { useFollowRequests } from '@/features/inspirations/api/follows.api';
import { useUnreadNotificationCount } from '@/features/social/api/notifications.api';
import { useSyncActions, useSyncState } from '@/hooks/useSyncEngine';
import { selectUser, useAuthStore } from '@/store/auth.store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AppShellProps {
  children: ReactNode;
}

interface NavItem {
  /** i18n key under the "nav" namespace */
  labelKey: string;
  icon: ReactNode;
  path: string;
  /** Extra route prefixes that should also light this tab. */
  alsoMatch?: string[];
}

// ---------------------------------------------------------------------------
// Navigation Configuration
// ---------------------------------------------------------------------------

// Five tabs, as on Instagram: Home is the feed and Search its own tab (the
// friends tab went when friends became follows, ADR-0007); the trip
// list has moved to its own tab and keeps '/trips/...' lit.
const NAV_ITEMS: NavItem[] = [
  {
    labelKey: 'nav.home',
    icon: <HomeIcon />,
    path: '/',
    alsoMatch: ['/inspirations', '/boards', '/places'],
  },
  { labelKey: 'nav.search', icon: <SearchIcon />, path: '/search' },
  { labelKey: 'nav.trips', icon: <TripsIcon />, path: '/trips' },
  { labelKey: 'nav.map', icon: <MapIcon />, path: '/map' },
  { labelKey: 'nav.profile', icon: <ProfileIcon />, path: '/profile', alsoMatch: ['/settings'] },
];

/**
 * Resolves the bottom navigation index based on the current route pathname.
 * Returns -1 if the current route doesn't match any primary nav item,
 * which hides the active indicator.
 */
function getActiveNavIndex(pathname: string): number {
  // Exact match first
  const exactIndex = NAV_ITEMS.findIndex((item) => item.path === pathname);
  if (exactIndex !== -1) return exactIndex;

  // Prefix match — e.g. /trips/abc → Home tab. '/' is excluded as a prefix
  // because it matches everything; its `alsoMatch` entries still count.
  return NAV_ITEMS.findIndex((item) =>
    [item.path, ...(item.alsoMatch ?? [])].some(
      (candidate) => candidate !== '/' && pathname.startsWith(candidate),
    ),
  );
}

/**
 * Map of route prefixes to i18n keys for the page title in the app bar.
 * The first match wins, so more specific prefixes should come first.
 */
const PAGE_TITLE_KEYS: [prefix: string, key: string][] = [
  ['/trips/new', 'appBar.newTrip'],
  ['/trips/', 'appBar.trip'],
  ['/search', 'nav.search'],
  ['/profile', 'nav.profile'],
  ['/map', 'appBar.myMap'],
  ['/notifications', 'appBar.notifications'],
  ['/settings', 'appBar.settings'],
  ['/ai/plan', 'appBar.aiPlanner'],
  ['/trips', 'appBar.myTrips'],
  ['/boards/', 'inspirations.boards.title'],
  ['/places/', 'inspirations.place.title'],
];

function getPageTitleKey(pathname: string): string {
  for (const [prefix, key] of PAGE_TITLE_KEYS) {
    if (pathname === prefix || (prefix.endsWith('/') && pathname.startsWith(prefix))) {
      return key;
    }
    if (pathname === prefix) {
      return key;
    }
  }
  return '__APP_NAME__';
}

// ---------------------------------------------------------------------------
// Routes where the bottom nav should be hidden (e.g. full-screen views)
// ---------------------------------------------------------------------------

const HIDE_BOTTOM_NAV_PATTERNS = [
  /^\/login/,
  /^\/register/,
  /^\/ai\/plan/,
  /^\/inspirations\//,
  /^\/welcome$/,
];

function shouldShowBottomNav(pathname: string): boolean {
  return !HIDE_BOTTOM_NAV_PATTERNS.some((pattern) => pattern.test(pathname));
}

// The branded top bar is currently home-only. Every other screen carries its own
// header, so a second bar above it just eats vertical space.
// Home only, as on Instagram: the logo and the notifications bell belong to the
// feed; every other tab carries its own title in the page.
function shouldShowAppBar(pathname: string): boolean {
  return pathname === '/';
}

// Routes that own the whole viewport. The map has to reach every screen edge,
// including under the status bar on iOS, so the shell stops reserving safe-area
// space and the page positions its own floating controls with env() insets.
// `/trips/:id/map` is gone — it redirects to `/map?trip=:id`, and a query
// string is not part of the pathname, so the plain `/map` pattern covers both.
// The trip page joins them: its cover photo runs to the very top, under the
// clock, and the shell reserving safe-area space would stop it short and leave
// a band of background above the image.
//
// The trip pattern excludes `new` explicitly. `/trips/:tripId` and `/trips/new`
// are the same shape, so the trip page's full-bleed rule was silently applying
// to the create form as well — which has no cover photo, just a heading that
// ended up underneath the clock.
// The welcome steps keep clear of the clock themselves, like the map.
const FULL_BLEED_PATTERNS = [
  /^\/map$/,
  /^\/profile\/[^/]+\/map$/,
  /^\/trips\/(?!new$)[^/]+$/,
  /^\/welcome$/,
];

function isFullBleed(pathname: string): boolean {
  return FULL_BLEED_PATTERNS.some((pattern) => pattern.test(pathname));
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APP_BAR_HEIGHT = 56;
// How far the feed must travel in one direction before the Home bar follows:
// hiding waits a little longer so a jittery thumb does not flicker it.
const APP_BAR_HIDE_AFTER = 12;
const APP_BAR_SHOW_AFTER = 6;
// Icons only: the labels went, and the bar gave back the room they took.
const BOTTOM_NAV_HEIGHT = 52;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppShell({ children }: AppShellProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const activeNavIndex = getActiveNavIndex(location.pathname);
  const currentUser = useAuthStore(selectUser);
  const pageTitleKey = getPageTitleKey(location.pathname);
  const pageTitle = pageTitleKey === '__APP_NAME__' ? 'Pintravel' : t(pageTitleKey);
  const showBottomNav = shouldShowBottomNav(location.pathname);
  const fullBleed = isFullBleed(location.pathname);
  const showAppBar = shouldShowAppBar(location.pathname);

  // The Home bar slides away while reading down and comes back on a small
  // scroll up, as on Instagram. The anchor is where the direction last turned.
  const [appBarHidden, setAppBarHidden] = useState(false);
  const scrollAnchor = useRef(0);
  const [barPath, setBarPath] = useState(location.pathname);
  if (barPath !== location.pathname) {
    setBarPath(location.pathname);
    setAppBarHidden(false);
  }
  const handleMainScroll = (event: UIEvent<HTMLElement>) => {
    if (!showAppBar) return;
    const y = event.currentTarget.scrollTop;
    if (y <= APP_BAR_HEIGHT) {
      scrollAnchor.current = y;
      setAppBarHidden(false);
    } else if (appBarHidden) {
      if (y > scrollAnchor.current) scrollAnchor.current = y;
      else if (scrollAnchor.current - y > APP_BAR_SHOW_AFTER) {
        scrollAnchor.current = y;
        setAppBarHidden(false);
      }
    } else if (y < scrollAnchor.current) {
      scrollAnchor.current = y;
    } else if (y - scrollAnchor.current > APP_BAR_HIDE_AFTER) {
      scrollAnchor.current = y;
      setAppBarHidden(true);
    }
  };

  // Real-time badge counts
  const { data: followRequests } = useFollowRequests();
  const { data: unreadNotifData } = useUnreadNotificationCount();
  const unreadNotifications = unreadNotifData?.data?.unreadCount ?? 0;
  // People asking to follow a private account; they are answered on the profile.
  const pendingFollowRequests = followRequests?.length ?? 0;

  // Offline sync state — drives the sync bar over the tab bar
  const syncStateData = useSyncState();
  const { syncNow } = useSyncActions();

  // Opened from the indicator when changes have been given up on. Without this
  // the red badge was a dead end: tapping it fell through to a plain resync,
  // which by definition skips the entries that made it red.
  const [failedChangesOpen, setFailedChangesOpen] = useState(false);

  // Build the SyncState shape expected by SyncStatusIndicator
  const syncState = {
    network: {
      isOnline: syncStateData.isOnline,
      connectionType: syncStateData.connectionType,
      lastOnlineAt: syncStateData.network.lastOnlineAt,
    },
    isSyncing: syncStateData.isSyncing,
    pendingCount: syncStateData.pendingCount,
    failedCount: syncStateData.failedCount ?? 0,
    lastSyncedAt: syncStateData.lastSyncedAt,
    lastError: syncStateData.lastError,
  };

  // Over the tab bar on every screen, and only when there is something to say.
  const syncBar = syncStateData.isInitialized ? (
    <SyncStatusIndicator
      syncState={syncState}
      onRequestSync={syncNow}
      onViewErrors={() => setFailedChangesOpen(true)}
    />
  ) : null;

  const handleNavChange = (_event: React.SyntheticEvent, newValue: number) => {
    const target = NAV_ITEMS[newValue];
    if (target) {
      navigate(target.path);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Use --app-height which tracks visual viewport (shrinks when keyboard opens)
        minHeight: 'var(--app-height, 100dvh)',
        height: 'var(--app-height, 100dvh)',
        maxHeight: 'var(--app-height, 100dvh)',
        // Animate height changes to match iOS keyboard animation (~0.25s)
        transition: 'height 0.25s ease-out, min-height 0.25s ease-out, max-height 0.25s ease-out',
        // Capacitor safe area support via env() — works on iOS notch devices
        paddingTop: showAppBar || fullBleed ? 0 : 'env(safe-area-inset-top)',
        paddingBottom: showBottomNav || fullBleed ? 0 : 'env(safe-area-inset-bottom)',
        bgcolor: 'background.default',
      }}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Top App Bar                                                        */}
      {/* ------------------------------------------------------------------ */}
      {showAppBar && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            paddingTop: 'env(safe-area-inset-top)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            color: 'text.primary',
            // Hidden, only the band under the clock stays, so the feed never
            // runs under the status bar.
            transform: appBarHidden ? `translateY(-${APP_BAR_HEIGHT}px)` : 'none',
            transition: 'transform 0.22s ease-out',
          }}
        >
          <Toolbar
            inert={appBarHidden}
            sx={{
              minHeight: APP_BAR_HEIGHT,
              px: { xs: 1.5, sm: 2 },
              opacity: appBarHidden ? 0 : 1,
              transition: 'opacity 0.18s ease-out',
            }}
          >
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <LogoLockup size={26} aria-label={pageTitle} sx={{ ml: 0.5 }} />
            </Box>

            {/* Notification Bell */}
            <IconButton
              size="large"
              aria-label={t('appBar.unreadNotifications', { count: unreadNotifications })}
              onClick={() => navigate('/notifications')}
              sx={{ color: 'text.secondary' }}
            >
              <Badge
                badgeContent={unreadNotifications}
                color="error"
                max={99}
                invisible={unreadNotifications === 0}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Settings (visible on desktop, hidden on mobile where it's in profile) */}
            {isDesktop && (
              <IconButton
                size="large"
                aria-label={t('appBar.settings')}
                onClick={() => navigate('/settings')}
                sx={{ color: 'text.secondary' }}
              >
                <SettingsIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Main Content Area                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Box
        component="main"
        className="app-main-content"
        onScroll={handleMainScroll}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          // Offset for the fixed app bar — padding, so the feed scrolls up under
          // it and fills the room it leaves when it slides away.
          pt: showAppBar ? `calc(${APP_BAR_HEIGHT}px + env(safe-area-inset-top))` : 0,
          // Offset for the fixed bottom nav — use padding (not margin) so it
          // is inside the scroll container and reliably pushes content up on
          // all browsers/devices.  Only needed on mobile where the nav shows.
          // Note: This padding is removed when keyboard is open via CSS
          pb:
            showBottomNav && !isDesktop
              ? `calc(${BOTTOM_NAV_HEIGHT}px + var(--bottom-nav-inset))`
              : 0,
          // Subtle horizontal padding on larger screens
          px: { xs: 0, md: 2 },
          // Center content on very wide screens
          maxWidth: isDesktop ? 1200 : '100%',
          mx: 'auto',
          width: '100%',
        }}
      >
        {children}
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Bottom Navigation                                                  */}
      {/* ------------------------------------------------------------------ */}
      {showBottomNav && (
        <Paper
          elevation={8}
          className="bottom-nav-container"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            paddingBottom: 'var(--bottom-nav-inset)',
            // Paper is rounded by default (18px, from the theme), which on a bar
            // pinned to the screen edge showed as two curved top corners floating
            // over square page content. A tab bar spans the edge — it has none.
            borderRadius: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
            // Subtle frosted-glass effect on supported browsers
            backdropFilter: 'blur(16px)',
            bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
          }}
        >
          {syncBar}
          <BottomNavigation
            value={activeNavIndex}
            onChange={handleNavChange}
            sx={{
              height: BOTTOM_NAV_HEIGHT,
              bgcolor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 0,
                py: 0,
                px: 1,
                color: 'text.secondary',
                transition: 'color 0.2s ease, transform 0.15s ease',
                '&.Mui-selected': {
                  color: 'primary.main',
                  // The theme pads the top for a label that is no longer there.
                  pt: 0,
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem',
                },
              },
            }}
          >
            {NAV_ITEMS.map((item, index) => {
              const label = t(item.labelKey);

              const badgeCount = item.path === '/profile' ? pendingFollowRequests : 0;
              // The profile tab is the person, as on Instagram: their picture (or
              // initials), ringed in coral when it is the open tab.
              const glyph =
                item.path === '/profile' && currentUser ? (
                  <Box
                    sx={{
                      display: 'flex',
                      borderRadius: '50%',
                      p: '2px',
                      border: '2px solid',
                      borderColor: index === activeNavIndex ? 'primary.main' : 'transparent',
                    }}
                  >
                    <UserAvatar
                      displayName={currentUser.displayName}
                      avatarUrl={currentUser.avatarUrl}
                      size={26}
                    />
                  </Box>
                ) : (
                  item.icon
                );

              const icon =
                badgeCount > 0 ? (
                  <Badge
                    badgeContent={badgeCount}
                    color="error"
                    max={99}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        height: 16,
                        minWidth: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    {glyph}
                  </Badge>
                ) : (
                  glyph
                );

              return (
                <BottomNavigationAction
                  key={item.path}
                  icon={icon}
                  aria-label={t('nav.navigateTo', { page: label })}
                />
              );
            })}
          </BottomNavigation>
        </Paper>
      )}

      {/* Without a tab bar the sync bar keeps to the bottom edge on its own. */}
      {!showBottomNav && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar,
            pb: 'env(safe-area-inset-bottom)',
          }}
        >
          {syncBar}
        </Box>
      )}

      <FailedChangesDialog
        open={failedChangesOpen}
        onClose={() => setFailedChangesOpen(false)}
        onRequestSync={syncNow}
      />
    </Box>
  );
}
